/**
 * Detail drawer for a selected ingress source.
 *
 * Shows current cursor, timestamps, last error, and offers the actions
 * the brief calls for: [Test connection], [Pause/Resume], [Edit],
 * [Delete]. The recent-activity panel queries the audit log filtered
 * by target_type=ingress_source / target_id=<id>.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { auditApi } from "@/lib/api-audit";
import { ingressApi, type IngressSource } from "@/lib/api-ingress";

export interface IngressDetailDrawerProps {
  source: IngressSource | null;
  onOpenChange: (open: boolean) => void;
  onEdit: (source: IngressSource) => void;
}

export function IngressDetailDrawer({
  source,
  onOpenChange,
  onEdit,
}: IngressDetailDrawerProps) {
  const qc = useQueryClient();
  const open = !!source;

  const auditQuery = useQuery({
    queryKey: ["audit", "ingress", source?.id],
    queryFn: () =>
      auditApi.list({
        target_type: "ingress_source",
        target_id: source!.id,
        page_size: 25,
      }),
    enabled: open,
  });

  // Inverted ownership: each workflow declares its source by putting a
  // typed SOURCE node in the DAG. We surface the consumer list so the
  // operator can navigate from a source to the workflows reading from it.
  const consumersQuery = useQuery({
    queryKey: ["ingress", "consumers", source?.id],
    queryFn: () => ingressApi.consumers(source!.id),
    enabled: open,
  });

  const testMutation = useMutation({
    mutationFn: () => ingressApi.test(source!.id),
  });

  const pauseMutation = useMutation({
    mutationFn: () =>
      source!.enabled ? ingressApi.pause(source!.id) : ingressApi.resume(source!.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ingress"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => ingressApi.remove(source!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ingress"] });
      onOpenChange(false);
    },
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        {source && (
          <>
            <SheetHeader>
              <SheetTitle className="text-sm font-semibold">
                <span className="font-mono text-xs text-muted-foreground">{source.type}</span>{" "}
                <span>{source.name}</span>
              </SheetTitle>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Badge tone={source.enabled ? "active" : "deprecated"}>
                  {source.enabled ? "enabled" : "paused"}
                </Badge>
                {source.last_error && <Badge tone="danger">last poll failed</Badge>}
              </div>
            </SheetHeader>

            <SheetBody className="space-y-4">
              <Section title="Timestamps">
                <KV k="Created" v={fmt(source.created_at)} />
                <KV k="Updated" v={fmt(source.updated_at)} />
                <KV k="Last poll" v={fmt(source.last_poll_at)} />
                <KV k="Last success" v={fmt(source.last_success_at)} />
              </Section>

              {source.last_error && (
                <Alert tone="danger" title="Last error">
                  <pre className="whitespace-pre-wrap text-xs">{source.last_error}</pre>
                </Alert>
              )}

              <Section title="Workflows consuming this source">
                {consumersQuery.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                ) : (consumersQuery.data ?? []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No workflow currently subscribes to this source.
                    Add a source node referencing it in the workflow editor.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {(consumersQuery.data ?? []).map((wf) => (
                      <li
                        key={wf.id}
                        className="flex items-center gap-2 border-b border-border/40 py-1 text-xs"
                      >
                        <a
                          href={`/workflows/${wf.id}`}
                          className="flex-1 font-medium hover:underline"
                        >
                          {wf.name}
                        </a>
                        <Badge tone="neutral">{wf.direction}</Badge>
                        {wf.active_version_number !== null && (
                          <span className="font-mono text-muted-foreground">
                            v{wf.active_version_number}
                          </span>
                        )}
                        {!wf.enabled && <Badge tone="warning">disabled</Badge>}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="Format">
                <pre className="overflow-x-auto rounded border border-border/60 bg-muted/30 p-2 font-mono text-xs">
                  {JSON.stringify(source.format, null, 2)}
                </pre>
              </Section>

              <Section title="Config">
                <pre className="overflow-x-auto rounded border border-border/60 bg-muted/30 p-2 font-mono text-xs">
                  {JSON.stringify(source.config, null, 2)}
                </pre>
              </Section>

              <Section title="Cursor">
                {source.cursor && Object.keys(source.cursor).length > 0 ? (
                  <pre className="overflow-x-auto rounded border border-border/60 bg-muted/30 p-2 font-mono text-xs">
                    {JSON.stringify(source.cursor, null, 2)}
                  </pre>
                ) : (
                  <p className="text-xs text-muted-foreground">(no cursor set yet)</p>
                )}
              </Section>

              {testMutation.data && (
                <Alert tone={testMutation.data.ok ? "info" : "danger"} title="Test result">
                  <pre className="overflow-x-auto whitespace-pre-wrap text-xs">
                    {JSON.stringify(testMutation.data, null, 2)}
                  </pre>
                </Alert>
              )}

              <Section title="Recent activity">
                {auditQuery.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                ) : (auditQuery.data?.items ?? []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No audit events yet.</p>
                ) : (
                  <ul className="space-y-1">
                    {(auditQuery.data?.items ?? []).map((evt) => (
                      <li
                        key={evt.audit_id}
                        className="flex items-center gap-2 border-b border-border/40 py-1 text-xs"
                      >
                        <span className="w-44 font-mono text-muted-foreground">
                          {fmt(evt.occurred_at)}
                        </span>
                        <span className="font-mono">{evt.event_type}</span>
                        <Badge tone={evt.outcome === "success" ? "active" : "danger"}>
                          {evt.outcome}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </SheetBody>

            <SheetFooter>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => testMutation.mutate()}
                disabled={testMutation.isPending}
              >
                {testMutation.isPending && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
                )}
                Test connection
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => pauseMutation.mutate()}
                disabled={pauseMutation.isPending}
              >
                {source.enabled ? "Pause" : "Resume"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => onEdit(source)}>
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (confirm(`Delete ingress source ${source.name}?`)) {
                    deleteMutation.mutate();
                  }
                }}
                className="text-danger"
              >
                Delete
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-28 text-muted-foreground">{k}</span>
      <span className="font-mono">{v}</span>
    </div>
  );
}

function fmt(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}
